/**
 * In-memory cache for API responses with TTL.
 *
 * REACT_APP_ENABLE_CACHE=true  → cache on (default)
 * REACT_APP_ENABLE_CACHE=false → cache off
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

const isCacheEnabled = (): boolean => {
  const envValue = process.env.REACT_APP_ENABLE_CACHE;
  if (envValue === undefined) return true;
  return envValue.toLowerCase() === 'true' || envValue === '1';
};

class APICache {
  private cache = new Map<string, CacheEntry<any>>();
  private pendingRequests = new Map<string, Promise<any>>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000;
  private cacheEnabled: boolean = isCacheEnabled();

  constructor() {
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log(`[APICache] enabled=${this.cacheEnabled}`);
    }
  }

  private isEnabled(): boolean {
    const current = isCacheEnabled();
    if (this.cacheEnabled && !current) {
      this.clear();
    }
    this.cacheEnabled = current;
    return current;
  }

  public getStatus(): { enabled: boolean; size: number } {
    return {
      enabled: this.isEnabled(),
      size: this.cache.size,
    };
  }

  public clearCache(): void {
    this.clear();
  }

  get<T>(key: string): T | null {
    if (!this.isEnabled()) return null;

    const entry = this.cache.get(key);
    if (!entry) return null;

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  set<T>(key: string, data: T, ttl: number = this.DEFAULT_TTL): void {
    if (!this.isEnabled()) return;

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  has(key: string): boolean {
    if (!this.isEnabled()) return false;

    const entry = this.cache.get(key);
    if (!entry) return false;

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  clear(): void {
    this.cache.clear();
  }

  getOrCreatePending<T>(key: string, factory: () => Promise<T>): Promise<T> {
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)!;
    }

    const promise = factory().finally(() => {
      this.pendingRequests.delete(key);
    });

    this.pendingRequests.set(key, promise);
    return promise;
  }
}

export const apiCache = new APICache();

export async function batchProcess<T, R>(
  items: T[],
  processor: (item: T) => Promise<R>,
  concurrency: number = 10
): Promise<R[]> {
  const results: R[] = [];
  const executing: Promise<void>[] = [];

  for (const item of items) {
    const promise = processor(item).then((result) => {
      results.push(result);
    });

    executing.push(promise);

    if (executing.length >= concurrency) {
      await Promise.race(executing);
      executing.splice(
        executing.findIndex((p) => p === promise),
        1
      );
    }
  }

  await Promise.all(executing);
  return results;
}
